// frontend/src/components/universal-audit/buildAuditPayload.js
// Results payload builder (localStorage)
// Contract goals (MVP):
// - Always produce stable "v2" blocks for Results hero + stacked view:
//   v2.structured_worst_case, v2.floor, v2.statutory_ceiling, v2.combined_stacked.per_regime
// - Preserve "scenarios" (normalized) including US_STATE aggregation_mode + state_breakdown when available
// - Store enforcement factors in the shape Results expects: inputs.enforcement_factors + inputs.severity_tier
// - Keep backward compatibility for older UI bits
// - Store Universal disclaimer acknowledgment for audit trail

import { currencyForScenarioKey, toNum } from "./helpers";

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return String(Date.now());
  }
}

function setToArray(x) {
  if (x instanceof Set) return Array.from(x);
  if (Array.isArray(x)) return x;
  return [];
}

function moneyNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function currencyObjToCodeSym(cur, fallbackKey = "US_FED") {
  const fb = currencyForScenarioKey(fallbackKey);
  return {
    currency_code: cur?.code || fb.code || "USD",
    currency_symbol: cur?.symbol || fb.symbol || "$",
  };
}

function scenarioKeyToFallbackCurrency(key) {
  if (key === "EU") return currencyForScenarioKey("EU");
  if (key === "UK") return currencyForScenarioKey("UK");
  return currencyForScenarioKey("US_FED");
}

function stableScenarioMeta(key) {
  switch (key) {
    case "US_FED":
      return { display_name: "US Federal", currency_code: "USD", currency_symbol: "$", jurisdiction_id: "us_federal" };
    case "US_STATE":
      return {
        display_name: "US State-by-State",
        currency_code: "USD",
        currency_symbol: "$",
        jurisdiction_id: "us_state_by_state",
      };
    case "EU":
      return { display_name: "European Union", currency_code: "EUR", currency_symbol: "€", jurisdiction_id: "eu_gdpr" };
    case "UK":
      return { display_name: "United Kingdom", currency_code: "GBP", currency_symbol: "£", jurisdiction_id: "uk_gdpr" };
    default:
      return { display_name: key, currency_code: "USD", currency_symbol: "$", jurisdiction_id: "" };
  }
}

// Map a V3 scenario into the normalized shape Results expects
function packScenarioV3(s, fallbackKey) {
  if (!s) return null;

  const meta = stableScenarioMeta(fallbackKey || "US_FED");

  const cur = {
    currency_code: s.currency_code || meta.currency_code || "USD",
    currency_symbol: s.currency_symbol || meta.currency_symbol || "$",
  };

  const statutoryCeiling = moneyNum(s.statutory_ceiling_visible_native);
  const floor = moneyNum(s.floor_native);
  const worst = moneyNum(s.worst_case_native);
  const net = moneyNum(s.net_native);

  return {
    enabled: !!s.enabled,
    display_name: s.display_name || meta.display_name || "",
    // Keep a stable jurisdiction id for stacked tiles + labels
    jurisdiction_id: s.regime_key || meta.jurisdiction_id || "",

    ...cur,

    // Canonical fields
    statutory_ceiling_visible_native: statutoryCeiling,
    floor_native: floor,
    worst_case_native: worst,
    net_native: net,

    // Compatibility aliases
    statutory_ceiling_native: statutoryCeiling,
    exposure_ceiling_native: worst,
    structured_worst_case_native: worst,
    net_risk_native: net,

    // US_STATE trace (only present when scenario provides it)
    aggregation_mode: s.aggregation_mode || null,
    states_selected: Array.isArray(s.states_selected) ? s.states_selected : [],
    state_breakdown: Array.isArray(s.state_breakdown) ? s.state_breakdown : [],
    state_max_basis: s.state_max_basis || null,
  };
}

// V2 fallback packer (older flows)
function packScenarioV2(s) {
  if (!s) return null;

  const cur = currencyObjToCodeSym(s.currency, "US_FED");

  const statutory_base = moneyNum(s.statutory_base_fine_native);
  const statutory_ceiling = moneyNum(s.statutory_ceiling_native);
  const worst_case = moneyNum(s.structured_worst_case_native ?? s.exposure_ceiling_native);
  const floor = moneyNum(s.floor_native);
  const net = moneyNum(s.net_risk_native);

  return {
    enabled: !!s.enabled,
    display_name: s.display_name || "",
    jurisdiction_id: s.jurisdiction_id || "",

    ...cur,

    statutory_base_fine_native: statutory_base,
    statutory_ceiling_native: statutory_ceiling,
    exposure_ceiling_native: worst_case,
    structured_worst_case_native: worst_case,
    floor_native: floor,
    net_risk_native: net,

    aggregation_mode: s.aggregation_mode || null,
    states_selected: Array.isArray(s.states_selected) ? s.states_selected : [],
    state_breakdown: Array.isArray(s.state_breakdown) ? s.state_breakdown : [],
    state_max_basis: s.state_max_basis || null,
  };
}

function firstEnabledScenarioKey(scenarios) {
  const order = ["US_FED", "US_STATE", "EU", "UK"];
  for (const k of order) {
    if (scenarios?.[k]?.enabled) return k;
  }
  // fall back
  return "US_FED";
}

function buildV2BlocksFromScenarios({ scenarios, headlineKey, headlineLabel, headlineCurrency }) {
  const hk = headlineKey || firstEnabledScenarioKey(scenarios);
  const hs = scenarios?.[hk] || null;

  const cur = {
    code: hs?.currency_code || headlineCurrency?.code || scenarioKeyToFallbackCurrency(hk)?.code || "USD",
    symbol: hs?.currency_symbol || headlineCurrency?.symbol || scenarioKeyToFallbackCurrency(hk)?.symbol || "$",
  };

  const structuredWorst =
    moneyNum(hs?.worst_case_native ?? hs?.structured_worst_case_native ?? hs?.exposure_ceiling_native) ?? 0;

  const floor =
    moneyNum(hs?.floor_native) ??
    (Number.isFinite(Number(structuredWorst)) ? Math.round(Number(structuredWorst) * 0.65) : 0);

  const statutoryCeiling =
    moneyNum(hs?.statutory_ceiling_visible_native ?? hs?.statutory_ceiling_native) ?? structuredWorst;

  // Combined stacked: include each enabled regime as its native currency tile
  const per_regime = [];
  const regimeKeys = ["US_FED", "US_STATE", "EU", "UK"];
  for (const k of regimeKeys) {
    const s = scenarios?.[k];
    if (!s?.enabled) continue;
    per_regime.push({
      scenario_key: k,
      display_name: s.display_name || stableScenarioMeta(k).display_name,
      jurisdiction_id: s.jurisdiction_id || stableScenarioMeta(k).jurisdiction_id || "",
      currency_code: s.currency_code || stableScenarioMeta(k).currency_code,
      currency_symbol: s.currency_symbol || stableScenarioMeta(k).currency_symbol,
      exposure_ceiling_native:
        moneyNum(s.worst_case_native ?? s.structured_worst_case_native ?? s.exposure_ceiling_native) ?? 0,
    });
  }

  return {
    structured_worst_case: {
      label: headlineLabel || "Structured Worst Case",
      exposure_native: structuredWorst,
      currency: cur,
    },
    floor: {
      label: "Floor",
      exposure_native: floor,
      currency: cur,
    },
    statutory_ceiling: {
      label: "Statutory Ceiling",
      exposure_native: statutoryCeiling,
      currency: cur,
    },
    combined_stacked: {
      label: "Combined Stacked Exposure",
      per_regime,
    },
  };
}

/**
 * buildAuditPayload(input)
 * Stores a Results-ready payload under STORAGE_KEY.
 *
 * V3-first:
 * - if input.v3_engine_output exists, use it directly,
 *   but ALWAYS emit v2 blocks (UI contract).
 * - otherwise fall back to V2 fields if present.
 */
export function buildAuditPayload(input = {}) {
  const {
    // raw inputs
    industry = "",
    niche = "",
    aiUseCase = "",
    peoplePerMonth = 0,
    annualRevenue = 0,

    selectedJurKeys,
    selectedStates,
    usStateAggregationMode,

    // v3 (preferred)
    v3_engine_output,

    // v2 (fallback)
    perRegime,
    headline,
    impactMultiplier,
    liveRiskMultiplier,

    // exec inputs (store snapshot)
    severityTier,
    enforcementInputs,

    // optional trace
    eventsSnapshot,
    importedBanner,

    // optional mode
    ui_mode,

    // NEW: disclaimer tracking
    disclaimer_acknowledged,
    disclaimer_text_version,
  } = input;

  const jurKeys = setToArray(selectedJurKeys);
  const states = setToArray(selectedStates);

  // Normalize enforcement factors to the keys Results reads today
  const normalizedSeverity =
    Number(severityTier) ||
    Number(enforcementInputs?.severityTier) ||
    Number(enforcementInputs?.severity_tier) ||
    null;

  const enforcement_factors = {
    violation_type: enforcementInputs?.violation_type || enforcementInputs?.violationType || null,
    intent: enforcementInputs?.intent || null,
    duration: enforcementInputs?.duration || null,
    cooperation: enforcementInputs?.cooperation || null,
    prior_enforcement: enforcementInputs?.prior_enforcement || enforcementInputs?.prior || null,
    governance_maturity: enforcementInputs?.governance_maturity || enforcementInputs?.governance || null,
  };

  // ----------------------------
  // V3 PATH
  // ----------------------------
  if (v3_engine_output && typeof v3_engine_output === "object") {
    const v3 = v3_engine_output;
    const v3Summary = v3.summary || {};
    const v3Scenarios = v3.scenarios || {};

    // Determine headline scenario key + currency
    const headlineKey = v3Summary.headline_basis || "US_FED";
    const headlineScenario = v3Scenarios[headlineKey] || null;

    const headlineCur = headlineScenario
      ? { code: headlineScenario.currency_code || "USD", symbol: headlineScenario.currency_symbol || "$" }
      : scenarioKeyToFallbackCurrency(headlineKey);

    // Normalize scenarios into a stable object
    const scenarios = {
      US_FED: packScenarioV3(v3Scenarios.US_FED, "US_FED"),
      US_STATE: packScenarioV3(v3Scenarios.US_STATE, "US_STATE"),
      EU: packScenarioV3(v3Scenarios.EU, "EU"),
      UK: packScenarioV3(v3Scenarios.UK, "UK"),

      // keep legacy combined placeholder if older UI references it
      EU_UK: {
        enabled: false,
        display_name: "EU + UK (combined view)",
        note: "",
      },
    };

    // Build mandatory v2 blocks for Results (even though engine is v3)
    const v2Blocks = buildV2BlocksFromScenarios({
      scenarios,
      headlineKey,
      headlineLabel: v3Summary.headline_label || "Structured Worst Case",
      headlineCurrency: headlineCur,
    });

    const payload = {
      generated_at: v3.generated_at || v3Summary.generated_at || nowIso(),

      inputs: {
        industry,
        niche,
        ai_use_case: aiUseCase,
        humans_impacted: Number(peoplePerMonth) || 0,
        annual_revenue_estimate: Number(annualRevenue) || 0,

        jurisdictions_selected: jurKeys,
        us_states_selected: states,
        us_state_aggregation_mode: String(usStateAggregationMode || "MAX"),

        // Results page currently reads these:
        severity_tier: normalizedSeverity,
        enforcement_factors,

        // NEW: disclaimer audit trail
        disclaimer_acknowledged: !!disclaimer_acknowledged,
        disclaimer_text_version: disclaimer_text_version || null,

        // Preserve detailed snapshot (safe for later Legal Mode expansion)
        executive_inputs: {
          ...(enforcementInputs || {}),
          severityTier: normalizedSeverity,
          view_mode: ui_mode || null,
        },
      },

      // V2 blocks (Results contract)
      v2: {
        structured_worst_case: v2Blocks.structured_worst_case,
        floor: v2Blocks.floor,
        statutory_ceiling: v2Blocks.statutory_ceiling,
        combined_stacked: v2Blocks.combined_stacked,

        // optional placeholders for future: trend points, citations, etc.
        trend: Array.isArray(v3Summary.trend) ? v3Summary.trend : [],
      },

      scenarios,

      summary: {
        engine_version: v3Summary.engine_version || "v3",
        generated_at: v3Summary.generated_at || nowIso(),

        currency_code: v2Blocks.structured_worst_case.currency.code,
        currency_symbol: v2Blocks.structured_worst_case.currency.symbol,

        headline_label: v3Summary.headline_label || "Structured Worst Case",
        headline_basis: headlineKey,

        headline_exposure_native: moneyNum(v3Summary.headline_exposure_native) ?? v2Blocks.structured_worst_case.exposure_native ?? 0,
        floor_native: moneyNum(v3Summary.floor_native) ?? v2Blocks.floor.exposure_native ?? null,
        statutory_ceiling_visible_native:
          moneyNum(v3Summary.statutory_ceiling_visible_native) ?? v2Blocks.statutory_ceiling.exposure_native ?? null,
        net_native: moneyNum(v3Summary.net_native),

        combined_stacked_by_currency:
          v3Summary.combined_stacked_by_currency || { USD: 0, EUR: 0, GBP: 0 },

        live_risk_multiplier: toNum(v3Summary.live_risk_multiplier ?? liveRiskMultiplier, 1),
        notes: Array.isArray(v3Summary.notes) ? v3Summary.notes : [],
      },

      eventsSnapshot: eventsSnapshot || null,
      importedSignal: importedBanner || null,
      ui_mode: ui_mode || null,
    };

    return payload;
  }

  // ----------------------------
  // V2 FALLBACK PATH (keeps older flows alive)
  // ----------------------------
  const headlineScenarioKey = headline?.scenario_key || "US_FED";
  const headlineCurrency =
    headline?.currency ||
    (headlineScenarioKey === "EU"
      ? currencyForScenarioKey("EU")
      : headlineScenarioKey === "UK"
      ? currencyForScenarioKey("UK")
      : currencyForScenarioKey("US_FED"));

  const cur = currencyObjToCodeSym(headlineCurrency, "US_FED");

  const headlineWorst = moneyNum(headline?.exposure);
  const statutoryCeiling = moneyNum(headline?.statutory_ceiling);
  const floor = moneyNum(headline?.floor);
  const net = moneyNum(headline?.net);

  const derivedFloor = floor ?? (headlineWorst == null ? null : Math.round(headlineWorst * 0.65));
  const derivedNet = net ?? headlineWorst ?? null;

  const scenarios = {
    US_FED: packScenarioV2(perRegime?.US_FED),
    US_STATE: packScenarioV2(perRegime?.US_STATE),
    EU: packScenarioV2(perRegime?.EU),
    UK: packScenarioV2(perRegime?.UK),
    EU_UK: {
      enabled: !!perRegime?.EU_UK?.enabled,
      display_name: perRegime?.EU_UK?.display_name || "EU + UK (combined view)",
      note: perRegime?.EU_UK?.note || "",
    },
  };

  // Ensure v2 blocks exist for Results even on fallback
  const v2Blocks = buildV2BlocksFromScenarios({
    scenarios,
    headlineKey: headlineScenarioKey,
    headlineLabel: headline?.label || "Structured Worst Case",
    headlineCurrency: { code: cur.currency_code, symbol: cur.currency_symbol },
  });

  const payload = {
    generated_at: nowIso(),
    inputs: {
      industry,
      niche,
      ai_use_case: aiUseCase,
      humans_impacted: Number(peoplePerMonth) || 0,
      annual_revenue_estimate: Number(annualRevenue) || 0,
      jurisdictions_selected: jurKeys,
      us_states_selected: states,

      severity_tier: normalizedSeverity,
      enforcement_factors,

      // NEW: disclaimer audit trail (fallback)
      disclaimer_acknowledged: !!disclaimer_acknowledged,
      disclaimer_text_version: disclaimer_text_version || null,

      executive_inputs: enforcementInputs || null,
    },

    v2: {
      structured_worst_case: v2Blocks.structured_worst_case,
      floor: v2Blocks.floor,
      statutory_ceiling: v2Blocks.statutory_ceiling,
      combined_stacked: v2Blocks.combined_stacked,
      trend: [],
    },

    scenarios,
    summary: {
      ...cur,
      headline_label: headline?.label || "Structured Worst Case (conservative)",
      headline_basis: headlineScenarioKey,

      headline_exposure_native: headlineWorst ?? 0,
      floor_native: derivedFloor ?? null,
      statutory_ceiling_visible_native: statutoryCeiling ?? null,
      net_native: derivedNet ?? null,

      impact_multiplier: toNum(impactMultiplier, 1),
      live_risk_multiplier: toNum(liveRiskMultiplier, 1),
    },
    eventsSnapshot: eventsSnapshot || null,
    importedSignal: importedBanner || null,
    ui_mode: ui_mode || null,
  };

  return payload;
}

export default buildAuditPayload;
